import { icon } from '../icons';
import { AMBIENT, mountAmbientBackground } from '../ambient-background';
import { initLandingAudio, createMuteToggle } from '../landing-audio';
import { mountClickSound } from '../click-sound';
import { mountHoloTilt } from '../holo-tilt';
import { LANDING_GLITCH_KINDS, mountRandomGlitch } from '../random-glitch';
import { mountLegalLinks } from '../legal-overlay';
import { FAQ, FINAL_ART, FOOTER_LINKS, HERO, HERO_ART, LEDES, PILLARS, PROGRESSION, STEPS, UNITS } from './landing-content';
import { api } from '../../core/network/api';
import { getSiteInfo } from '../../core/preview/preview';
import { getLocale, setLocale, t, type Locale } from '../i18n/i18n';
import { languageSwitcherHtml } from '../i18n/language-switcher';
import { socialLinkHtml } from '../social-networks';

function escapeHtml(value: string): string {
  const div = document.createElement('div');
  div.textContent = value;
  return div.innerHTML;
}

const SECTION = 'ldg-wrap';
/**
 * Landing blocks that lean toward the pointer (one delegated listener; see holo-tilt.ts): the self-contained cards only.
 * Never the hero art (jittered, looked wrong), never list rows (they get the flat `.ldg-holo-row` edge instead), never anything holding an input.
 */
const HOLO_TILT_SELECTOR = '.ldg-bay, .ldg-dossier, .ldg-waypoint';

/** The nav, declared once — the header and the mobile panel render the same set. */
const NAV_LINKS: { href: string; label: () => string; scroll: boolean }[] = [
  { href: '#how', label: () => t('landing.nav.how'), scroll: true },
  { href: '#units', label: () => t('landing.nav.arsenal'), scroll: true },
  { href: '#progression', label: () => t('landing.nav.base'), scroll: true },
  { href: '#why', label: () => t('landing.nav.rules'), scroll: true },
  { href: '#faq', label: () => t('landing.nav.faq'), scroll: true },
  { href: '/news', label: () => t('landing.nav.news'), scroll: false },
];

function navLinksHtml(linkClass: string): string {
  return NAV_LINKS.map(
    (l) =>
      `<a href="${l.href}"${l.scroll ? ' data-action="scroll-to"' : ''} class="${linkClass}">${escapeHtml(l.label())}</a>`,
  ).join('');
}

function waypointsHtml(): string {
  return STEPS.map(
    (s, i) => `
      <li data-step>
        <div class="ldg-waypoint">
          <span class="ldg-waypoint-badge"><span>${i + 1}</span></span>
          <div>
            <h3 class="ldg-waypoint-title">${escapeHtml(s.title)}</h3>
            <p class="ldg-waypoint-body">${escapeHtml(s.body)}</p>
          </div>
        </div>
      </li>`,
  ).join('');
}

function bayFiguresHtml(): string {
  return UNITS.map(
    (u, i) => `
      <figure class="ldg-bay-figure ${i === 0 ? 'is-active' : ''}" data-bay-figure>
        <img src="${u.image}" alt="${escapeHtml(u.name)}" loading="${i === 0 ? 'eager' : 'lazy'}" />
      </figure>`,
  ).join('');
}

function rosterButtonsHtml(): string {
  return UNITS.map(
    (u, i) => `
      <button type="button" role="tab" class="ldg-roster-btn"
        data-unit data-action="select-unit" data-unit-index="${i}"
        aria-selected="${i === 0}" aria-controls="bay" tabindex="${i === 0 ? '0' : '-1'}"
        aria-label="${escapeHtml(u.name)}, ${escapeHtml(u.category)}">
        <img src="${u.image}" alt="" loading="lazy" />
      </button>`,
  ).join('');
}

function dossierPanelsHtml(): string {
  return UNITS.map(
    (u, i) => `
      <div class="ldg-dossier-text" data-dossier-panel ${i === 0 ? '' : 'hidden'} aria-live="polite">
        <p class="ldg-dossier-class">${escapeHtml(u.category)}</p>
        <h3 class="ldg-dossier-name">${escapeHtml(u.name)}</h3>
        <p class="ldg-dossier-desc">${escapeHtml(u.description)}</p>
      </div>`,
  ).join('');
}

function stageReadoutPanelsHtml(): string {
  return PROGRESSION.map(
    (p, i) => `
      <div class="ldg-stage-readout" data-stage-readout ${i === 0 ? '' : 'hidden'} aria-live="polite">
        <p class="ldg-stage-count">${t('landing.progression.stageOf', { n: i + 1, m: PROGRESSION.length })}</p>
        <h3 class="ldg-stage-name">${escapeHtml(p.label)}</h3>
        <p class="ldg-stage-desc">${escapeHtml(p.description)}</p>
      </div>`,
  ).join('');
}

function trackButtonsHtml(): string {
  return PROGRESSION.map(
    (p, i) => `
      <button type="button" class="ldg-track-btn" data-progression data-action="select-stage" data-stage-index="${i}" aria-pressed="${i === 0}">
        <span class="ldg-track-pip">${i + 1}</span>
        <span class="ldg-track-label">${escapeHtml(p.label)}</span>
      </button>`,
  ).join('');
}

/**
 * The four stages as scenes on one plot. Visibility is a class, not `hidden`,
 * so the stages crossfade instead of hard-cutting — `hidden` would kill the
 * opacity/transform transition that makes the base look like it is growing.
 */
function plotHtml(): string {
  const scenes = PROGRESSION.map(
    (p, i) => `
      <div class="ldg-scene ${i === 0 ? 'is-active' : ''}" data-stage-row>
        ${p.images
          .map(
            (img) =>
              `<img src="${img.src}" alt="" loading="lazy" style="--x:${img.x};--y:${img.y};--w:${img.w}" />`,
          )
          .join('')}
      </div>`,
  ).join('');

  return `
    <div class="ldg-plot" aria-hidden="true">
      <div class="ldg-plot-ground">
        <svg viewBox="0 0 200 100" preserveAspectRatio="none">
          <polygon points="100,2 198,50 100,98 2,50" fill="var(--color-surface-2)"
            stroke="var(--color-border)" stroke-width=".6" vector-effect="non-scaling-stroke" />
          <polygon points="100,14 174,50 100,86 26,50" fill="none"
            stroke="var(--color-border)" stroke-width=".6" stroke-dasharray="3 3" vector-effect="non-scaling-stroke" />
        </svg>
      </div>
      ${scenes}
    </div>`;
}

function rulesHtml(): string {
  return PILLARS.map(
    (p) => `
      <li data-pillar class="ldg-holo-row">
        <span class="text-2xl text-gold shrink-0 mt-0.5" aria-hidden="true">${icon(p.icon)}</span>
        <div class="min-w-0">
          <h3 class="ldg-rule-title">${escapeHtml(p.title)}</h3>
          <p class="ldg-rule-body">${escapeHtml(p.body)}</p>
        </div>
      </li>`,
  ).join('');
}

/**
 * The FAQ, from the CMS when an admin has published entries and from the
 * built-in bilingual copy otherwise.
 *
 * The fallback is deliberate rather than defensive: `FaqEntry` is a
 * single-language table, so a fresh install with an empty CMS would show
 * nothing at all, and a Spanish visitor would lose the translated copy in
 * `landing-content.ts`. Published CMS entries win because an admin editing
 * them expects to see the result.
 */
function faqHtml(items: { q: string; a: string }[] = FAQ): string {
  return items.map(
    (f, i) => `
      <div class="border-b border-border ldg-holo-row" data-faq>
        <button type="button" data-action="toggle-faq" data-faq-index="${i}"
          aria-expanded="false" aria-controls="faq-answer-${i}"
          class="w-full min-h-11 py-5 flex items-start justify-between gap-4 text-left touch-manipulation">
          <span class="ldg-display text-base sm:text-lg text-text">${escapeHtml(f.q)}</span>
          <span data-faq-marker class="ldg-faq-marker" aria-hidden="true"></span>
        </button>
        <div id="faq-answer-${i}" data-faq-answer hidden class="pb-5">
          <p class="text-sm text-text-dim leading-relaxed max-w-prose">${escapeHtml(f.a)}</p>
        </div>
      </div>`,
  ).join('');
}

interface LegalPage {
  slug: string;
  title: string;
}

interface SocialLink {
  platform: string;
  url: string;
  icon: string;
}

/**
 * Registered-company line (name, address, registration number,
 * jurisdiction) from `GET /api/site-info` — already fetched at app boot
 * (main.ts), so this reads the cache rather than issuing its own request.
 * Empty in preview mode and in any environment where the admin hasn't
 * filled in the Company settings panel yet; returns '' rather than a row of
 * blank/undefined text in that case.
 */
function companyInfoHtml(): string {
  const company = getSiteInfo()?.company;
  if (!company) return '';
  const parts = [
    company.name,
    company.address,
    company.registration ? `Reg. ${company.registration}` : '',
    company.jurisdiction,
  ].filter((p) => p.length > 0);
  if (parts.length === 0) return '';
  return `<p class="text-[10px] text-text-dim text-center sm:text-left">${parts.map(escapeHtml).join(' &middot; ')}</p>`;
}

async function fetchFooterData(): Promise<{ legal: LegalPage[]; social: SocialLink[] }> {
  // '/cms/legal-pages' (game/legal-pages, not core) — core's own CMS only
  // serves one legal page by slug (GET /cms/legal/:slug), with no public
  // list-all route; this game module adds that list without editing core.
  const [legalRes, socialRes] = await Promise.allSettled([
    api.get<LegalPage[]>('/cms/legal-pages'),
    api.get<SocialLink[]>('/cms/social-links'),
  ]);

  return {
    legal: legalRes.status === 'fulfilled' ? legalRes.value : [],
    social: socialRes.status === 'fulfilled' ? socialRes.value : [],
  };
}

function selectUnit(root: HTMLElement, index: number): void {
  const figures = root.querySelectorAll('[data-bay-figure]');
  const buttons = Array.from(root.querySelectorAll('[data-unit]')) as HTMLButtonElement[];
  const panels = root.querySelectorAll('[data-dossier-panel]');
  const tag = root.querySelector('[data-bay-tag]');
  figures.forEach((f, i) => f.classList.toggle('is-active', i === index));
  buttons.forEach((b, i) => {
    b.setAttribute('aria-selected', String(i === index));
    b.tabIndex = i === index ? 0 : -1;
  });
  panels.forEach((p, i) => { (p as HTMLElement).hidden = i !== index; });
  if (tag) tag.textContent = t('landing.arsenal.unitOf', { n: index + 1, m: buttons.length });
}

function selectStage(root: HTMLElement, index: number): void {
  const rows = root.querySelectorAll('[data-stage-row]');
  const buttons = Array.from(root.querySelectorAll('[data-progression]')) as HTMLButtonElement[];
  const readouts = root.querySelectorAll('[data-stage-readout]');
  const fill = root.querySelector('[data-track-fill]') as HTMLElement | null;
  // Scenes crossfade (class), readouts swap outright (hidden) — a readout is
  // text in an aria-live region, so it must actually leave the a11y tree.
  rows.forEach((r, i) => r.classList.toggle('is-active', i === index));
  readouts.forEach((r, i) => { (r as HTMLElement).hidden = i !== index; });
  buttons.forEach((b, i) => {
    b.setAttribute('aria-pressed', String(i === index));
    b.classList.toggle('is-done', i < index);
  });
  if (fill && buttons.length > 1) {
    fill.style.width = `${(index / (buttons.length - 1)) * 100}%`;
  }
}

/**
 * `onPlay` navigates into the app's own router rather than following a link,
 * so the CTA does not reload the page and lose the session bootstrap.
 */
export function renderLandingScreen(root: HTMLElement, onPlay: () => void): () => void {
  const year = new Date().getFullYear();

  root.innerHTML = `
    <div class="w-full h-full overflow-y-auto bg-bg text-text" data-scroll-root>
      <!-- Sticky, not the reference's fixed: the scrolling element here is the
           inner [data-scroll-root] div rather than the document, so a fixed
           header would detach from the context it belongs to. -->
      <header class="sticky top-0 z-30 bg-bg/90 backdrop-blur border-b border-border relative" data-header>
        <div class="${SECTION} ldg-header-row">
          <a href="/" class="ldg-header-logo flex items-center min-w-0">
            <!-- Hidden until the hero's own logo scrolls out from under this
                 sticky header — see the IntersectionObserver wired up below.
                 Occupies its layout space from the start (opacity only, no
                 display toggle) so the header's height and the nav/button
                 positions never jump when it fades in. -->
            <img src="/brand/logo-wide-sm.webp" alt="Dune in Conflict" class="header-logo h-10 sm:h-[60px] w-auto shrink-0" />
          </a>
          <nav class="ldg-nav" aria-label="${t('landing.sections')}">${navLinksHtml('')}</nav>
          <div class="ldg-header-actions">
            ${languageSwitcherHtml('compact')}
            <button type="button" data-action="play" class="ldg-btn ldg-btn-primary touch-manipulation">
              ${HERO.primaryCta}
            </button>
            <button type="button" class="ldg-menu-btn touch-manipulation"
              data-action="toggle-menu" data-menu-btn
              aria-expanded="false" aria-controls="ldg-mobile-nav" aria-label="${t('landing.nav.menu')}">
              <span class="text-xl" aria-hidden="true">${icon('menu')}</span>
            </button>
          </div>
        </div>
        <nav id="ldg-mobile-nav" class="ldg-mobile-nav hidden" aria-label="${t('landing.sections')}" data-mobile-nav>
          ${navLinksHtml('')}
          <div class="ldg-mobile-lang">${languageSwitcherHtml('compact')}</div>
        </nav>
      </header>

      <section class="relative min-h-[72vh] flex items-center overflow-hidden">
        <div data-ambient class="absolute inset-0 overflow-hidden pointer-events-none"></div>
        <div class="${SECTION} relative py-16 sm:py-24">
          <div class="ldg-hero-grid">
            <div class="ldg-hero-copy flex flex-col gap-5 min-w-0 max-w-xl">
              <!-- The observed sentinel. Once this scrolls up far enough to sit
                   under the sticky header, the header's own logo fades in and
                   this one fades out — see the IntersectionObserver below.
                   Starts fully visible, matching the header logo's "starts
                   hidden" default, so the handoff is a clean crossfade rather
                   than a flash of both or neither. -->
              <div data-hero-logo>
                <img src="/brand/logo-wide.webp" alt="Dune in Conflict" class="hero-logo h-14 sm:h-20 w-auto" />
              </div>
              <p class="font-heading text-[10px] text-gold tracking-[0.18em] uppercase break-words">${escapeHtml(HERO.kicker)}</p>
              <h1 class="ldg-display text-2xl sm:text-4xl md:text-5xl text-text break-words">${escapeHtml(HERO.title)}</h1>
              <p class="text-sm text-text-dim leading-relaxed max-w-prose">${escapeHtml(HERO.sub)}</p>
              <div class="flex flex-wrap gap-3 pt-1">
                <button type="button" data-action="play" class="ldg-btn ldg-btn-primary ldg-btn-lg touch-manipulation">
                  ${HERO.primaryCta}
                </button>
                <a href="#how" data-action="scroll-to" class="ldg-btn ldg-btn-ghost ldg-btn-lg touch-manipulation">
                  ${HERO.secondaryCta}
                </a>
              </div>
            </div>
            <div class="ldg-hero-art" aria-hidden="true">
              <div class="ldg-art-shadow"></div>
              <img class="ldg-art-fortress" src="${HERO_ART.fortress}" alt="" />
              <img class="ldg-art-tank" src="${HERO_ART.tank}" alt="" />
              <img class="ldg-art-gunship" src="${HERO_ART.gunship}" alt="" />
            </div>
          </div>
        </div>
      </section>

      <section id="how" class="${SECTION} py-14 sm:py-20 flex flex-col gap-10 scroll-mt-16">
        <div class="ldg-section-head">
          <h2 class="ldg-display text-2xl sm:text-3xl text-gold landing-heading">${t('landing.section.how')}</h2>
          <p class="ldg-lede">${escapeHtml(LEDES.how)}</p>
        </div>
        <ol class="ldg-route">${waypointsHtml()}</ol>
      </section>

      <section id="units" class="py-14 sm:py-20 flex flex-col gap-10 scroll-mt-16">
        <div class="${SECTION} ldg-section-head">
          <h2 class="ldg-display text-2xl sm:text-3xl text-gold landing-heading">${t('landing.section.arsenal')}</h2>
          <p class="ldg-lede">${escapeHtml(LEDES.arsenal)}</p>
        </div>
        <div class="${SECTION}">
          <div class="ldg-hangar">
            <div class="ldg-bay" id="bay" data-bay>
              <div class="ldg-bay-corners" aria-hidden="true"></div>
              ${bayFiguresHtml()}
              <span class="ldg-bay-tag" data-bay-tag>${t('landing.arsenal.unitOf', { n: 1, m: UNITS.length })}</span>
            </div>
            <div class="ldg-dossier" data-dossier>
              ${dossierPanelsHtml()}
              <div class="mt-auto">
                <div class="ldg-roster" role="tablist" aria-label="${t('landing.section.arsenal')}" data-roster>${rosterButtonsHtml()}</div>
                <p class="ldg-roster-note" data-roster-note>${t('landing.arsenal.legend')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="progression" class="${SECTION} py-14 sm:py-20 flex flex-col gap-10 scroll-mt-16">
        <h2 class="ldg-display text-2xl sm:text-3xl text-gold landing-heading">${t('landing.section.progression')}</h2>
        <div class="ldg-growth">
          <div class="flex flex-col">
            ${stageReadoutPanelsHtml()}
            <div class="ldg-track" aria-label="${t('landing.nav.base')}">
              <span class="ldg-track-fill" data-track-fill aria-hidden="true"></span>
              ${trackButtonsHtml()}
            </div>
          </div>
          ${plotHtml()}
        </div>
      </section>

      <section id="why" class="border-y border-border bg-surface-2/40 scroll-mt-16">
        <div class="${SECTION} py-14 sm:py-20 ldg-rules">
          <div class="ldg-rules-head">
            <h2 class="ldg-display text-2xl sm:text-3xl text-gold landing-heading">${t('landing.section.rules')}</h2>
            <p class="ldg-lede">${escapeHtml(LEDES.rules)}</p>
          </div>
          <ul class="ldg-rule-list">${rulesHtml()}</ul>
        </div>
      </section>

      <section id="faq" class="${SECTION} py-14 sm:py-20 flex flex-col gap-4 scroll-mt-16">
        <h2 class="ldg-display text-2xl sm:text-3xl text-gold landing-heading">${t('landing.section.faq')}</h2>
        <div class="border-t border-border" data-faq-list>${faqHtml()}</div>
      </section>

      <section class="ldg-final">
        <div class="${SECTION} py-16 sm:py-24 ldg-final-grid">
          <div class="ldg-final-copy flex flex-col items-start gap-6">
            <h2 class="ldg-display ldg-final-title text-text max-w-2xl break-words">${t('landing.closing')}</h2>
            <button type="button" data-action="play" class="ldg-btn ldg-btn-primary ldg-btn-lg touch-manipulation">
              ${HERO.primaryCta}
            </button>
          </div>
          <div class="ldg-final-art" aria-hidden="true"><img src="${FINAL_ART}" alt="" /></div>
        </div>
        <svg class="ldg-final-dunes" viewBox="0 0 1440 90" preserveAspectRatio="none" aria-hidden="true">
          <path d="M0 50 C 240 20 480 60 720 40 S 1200 20 1440 45 L1440 90 L0 90Z" fill="var(--color-bg)" />
        </svg>
      </section>

      <footer class="border-t border-border">
        <div class="${SECTION} py-8 flex flex-col gap-4">
          <div class="flex flex-col items-center sm:flex-row sm:items-center justify-between gap-6 sm:gap-4">
            <div class="flex flex-col items-center gap-2 text-center sm:flex-row sm:gap-3 sm:text-left min-w-0">
              <img src="/brand/logo-wide-sm.webp" alt="" aria-hidden="true" class="h-16 sm:h-9 w-auto shrink-0" />
              <span class="text-[10px] text-text-dim">&copy; ${year} &middot; ${t('landing.rights')}</span>
            </div>
            <nav class="grid grid-cols-2 gap-x-6 gap-y-3 w-full max-w-xs text-center sm:flex sm:flex-wrap sm:w-auto sm:max-w-none sm:text-left sm:gap-4 text-xs text-text-dim" aria-label="${t('landing.footer')}" data-footer-nav>
              ${navLinksHtml('hover:text-text transition-colors duration-[120ms]')}
              ${FOOTER_LINKS.map((l) => `<a href="${l.href}" class="hover:text-text transition-colors duration-[120ms]">${escapeHtml(l.label)}</a>`).join('')}
            </nav>
          </div>
          ${companyInfoHtml()}
          <div data-footer-legal class="flex flex-wrap justify-center sm:justify-start gap-4 text-[10px] text-text-dim"></div>
          <div data-footer-social class="flex flex-wrap justify-center sm:justify-start gap-1"></div>
        </div>
      </footer>
    </div>
  `;

  mountAmbientBackground(root.querySelector('[data-ambient]') as HTMLElement, AMBIENT.battle);

  const scrollRoot = root.querySelector('[data-scroll-root]') as HTMLElement;

  /**
   * Hero logo -> header logo handoff, ported from steel-of-war-core
   * (frontend/src/landing/LandingScreen.vue + landing.css there). One
   * IntersectionObserver on the hero logo's sentinel flips one class on the
   * header; both logos' fade/scale is plain CSS driven off that one class
   * (see .header-logo / .hero-logo in app.css) — never per-element JS state.
   *
   * No custom `root` option: the default (browser) viewport is correct here
   * the same way it is in steel-of-war-core, even though `data-scroll-root`
   * is its own scrolling element — IntersectionObserver's default root is
   * the nearest scrollable ancestor's containing block, which resolves
   * correctly either way since the sentinel's scroll position relative to
   * the VIEWPORT (where the sticky header actually sits) is what matters,
   * not its position within the inner scroll container.
   */
  const headerEl = root.querySelector('[data-header]') as HTMLElement;
  const heroLogoSentinel = root.querySelector('[data-hero-logo]') as HTMLElement | null;
  let logoObserver: IntersectionObserver | null = null;
  // Two classes, not one: the header and the hero logo are SIBLING subtrees
  // (both direct children of [data-scroll-root]), not ancestor/descendant, so
  // a single toggled class has no CSS selector that reaches both from one
  // place. Each one's own class drives its own crossfade rule in app.css.
  if (heroLogoSentinel && 'IntersectionObserver' in window) {
    logoObserver = new IntersectionObserver(
      ([entry]) => {
        const scrolled = !entry.isIntersecting;
        headerEl.classList.toggle('header-scrolled', scrolled);
        heroLogoSentinel.classList.toggle('hero-logo-hidden', scrolled);
      },
      // Shrinks the viewport's effective top edge by roughly the sticky
      // header's own height, so the crossfade fires once the hero logo is
      // actually hidden behind it, not a frame before.
      { threshold: 0, rootMargin: '-72px 0px 0px 0px' },
    );
    logoObserver.observe(heroLogoSentinel);
  } else {
    // No IntersectionObserver support: default to the header logo already
    // visible rather than stranding the player with no logo anywhere.
    headerEl.classList.add('header-scrolled');
    heroLogoSentinel?.classList.add('hero-logo-hidden');
  }

  void fetchFooterData().then(({ legal, social }) => {
    const legalEl = root.querySelector('[data-footer-legal]');
    if (legalEl && legal.length > 0) {
      legalEl.innerHTML = legal
        .map((p) => `<a href="/legal/${escapeHtml(p.slug)}" class="hover:text-text transition-colors duration-[120ms]">${escapeHtml(p.title)}</a>`)
        .join('');
    }
    const socialEl = root.querySelector('[data-footer-social]');
    if (socialEl && social.length > 0) {
      // Icon, hover colour and accessible name come from the shared catalogue;
      // rows with an unknown network or a non-http(s) URL render nothing.
      socialEl.innerHTML = social.map((s) => socialLinkHtml(s, escapeHtml)).join('');
    }
  });

  // CMS-managed FAQ, swapped in only when an admin has published entries.
  // Patching the list rather than re-rendering the section keeps any answer
  // the visitor already expanded, and leaves the built-in copy in place when
  // the request fails or returns nothing.
  void api
    .get<{ question: string; answer: string }[]>('/cms/faq')
    .then((entries) => {
      const list = Array.isArray(entries) ? entries : [];
      if (list.length === 0) return;
      const el = root.querySelector('[data-faq-list]');
      if (el) el.innerHTML = faqHtml(list.map((e) => ({ q: e.question, a: e.answer })));
    })
    .catch(() => {
      /* keep the built-in bilingual FAQ */
    });

  // The footer links to /legal/<slug>, which main.ts has no route for; this
  // turns them into overlays instead of a redirect to /login.
  const legalTeardown = mountLegalLinks(root);

  /**
   * Mobile nav disclosure. Modelled on the audio popup in `engine/shell.ts`:
   * one toggle carrying `aria-expanded`, a panel shown by removing `hidden`,
   * dismissed by Escape or a click outside the header.
   *
   * Outside-click is detected with `headerEl.contains()` rather than by
   * stopping propagation on the toggle — stopPropagation would suppress the
   * document listener as a side effect, which happens to work but hides the
   * intent and breaks the moment anything else listens at that level.
   */
  const mobileNav = root.querySelector('[data-mobile-nav]') as HTMLElement | null;
  const menuBtn = root.querySelector('[data-menu-btn]') as HTMLElement | null;

  function setMenuOpen(open: boolean): void {
    if (!mobileNav || !menuBtn) return;
    mobileNav.classList.toggle('hidden', !open);
    menuBtn.setAttribute('aria-expanded', String(open));
    menuBtn.setAttribute('aria-label', open ? t('landing.nav.closeMenu') : t('landing.nav.menu'));
  }
  function isMenuOpen(): boolean {
    return menuBtn?.getAttribute('aria-expanded') === 'true';
  }
  function onDocClickForMenu(e: MouseEvent): void {
    if (!isMenuOpen()) return;
    if (!headerEl.contains(e.target as Node)) setMenuOpen(false);
  }
  function onDocKeydownForMenu(e: KeyboardEvent): void {
    if (e.key === 'Escape' && isMenuOpen()) setMenuOpen(false);
  }
  document.addEventListener('click', onDocClickForMenu);
  document.addEventListener('keydown', onDocKeydownForMenu);

  function onClick(e: MouseEvent): void {
    const el = (e.target as HTMLElement).closest('[data-action]') as HTMLElement | null;
    if (!el) return;

    if (el.dataset.action === 'play') {
      onPlay();
      return;
    }

    if (el.dataset.action === 'toggle-menu') {
      setMenuOpen(!isMenuOpen());
      return;
    }

    if (el.dataset.action === 'set-locale') {
      const next = el.dataset.locale as Locale | undefined;
      if (!next || next === getLocale()) return;
      setLocale(next);
      // Reload rather than re-render: the landing's copy lives in a typed
      // content module picked at import time, and the ambient video/audio
      // would have to be torn down and re-armed anyway. See the same call in
      // settings-screen.ts for the full reasoning.
      window.location.reload();
      return;
    }

    if (el.dataset.action === 'scroll-to') {
      e.preventDefault();
      // Tapping a link in the mobile panel must close it, or the panel stays
      // open covering the very section it just scrolled to.
      setMenuOpen(false);
      const href = el.getAttribute('href');
      if (!href) return;
      const target = scrollRoot.querySelector(href) as HTMLElement | null;
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      return;
    }

    if (el.dataset.action === 'toggle-faq') {
      const wrapper = el.closest('[data-faq]') as HTMLElement;
      const answer = wrapper.querySelector('[data-faq-answer]') as HTMLElement;
      const marker = el.querySelector('[data-faq-marker]') as HTMLElement;
      const open = el.getAttribute('aria-expanded') === 'true';
      el.setAttribute('aria-expanded', String(!open));
      answer.hidden = open;
      marker.classList.toggle('is-open', !open);
      return;
    }

    if (el.dataset.action === 'select-unit') {
      selectUnit(root, Number(el.dataset.unitIndex));
      return;
    }

    if (el.dataset.action === 'select-stage') {
      selectStage(root, Number(el.dataset.stageIndex));
      return;
    }
  }

  root.addEventListener('click', onClick);

  /**
   * Arrow-key roster navigation (left/right/up/down/home/end), scoped to the
   * roster element rather than folded into the delegated click handler above
   * — keydown and click are different event types with different targets
   * (a key always fires on the focused button itself), so delegating both
   * through one listener would need its own target-matching branch anyway.
   */
  const rosterEl = root.querySelector('[data-roster]') as HTMLElement | null;
  function onRosterKeydown(e: KeyboardEvent): void {
    const buttons = Array.from(root.querySelectorAll('[data-unit]')) as HTMLButtonElement[];
    const current = (e.target as HTMLElement).closest('[data-unit]') as HTMLButtonElement | null;
    if (!current) return;
    const i = buttons.indexOf(current);
    let next: number | null = null;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (i + 1) % buttons.length;
    if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (i - 1 + buttons.length) % buttons.length;
    if (e.key === 'Home') next = 0;
    if (e.key === 'End') next = buttons.length - 1;
    if (next !== null) {
      e.preventDefault();
      selectUnit(root, next);
      buttons[next].focus();
    }
  }
  rosterEl?.addEventListener('keydown', onRosterKeydown);

  const audioTeardown = initLandingAudio();
  const muteToggle = createMuteToggle();
  document.body.appendChild(muteToggle.el);
  const clickTeardown = mountClickSound(root);

  // Hologram skin, same as in the game: panels lean toward the pointer, a projector scan band
  // crawls down the viewport, headings carry the chromatic split, and a random few elements
  // glitch now and then. All of it stands down for reduced motion (see glitch.ts / holo-tilt.ts).
  const scan = document.createElement('div');
  scan.className = 'holo-scan';
  scan.setAttribute('aria-hidden', 'true');
  document.body.appendChild(scan);
  root.querySelectorAll(HOLO_TILT_SELECTOR).forEach((el) => {
    el.setAttribute('data-holo-tilt', '');
    el.classList.add('ldg-holo');
  });
  root.querySelectorAll('.ldg-hero-copy h1, .landing-heading').forEach((el) => el.classList.add('holo-title'));
  const tiltTeardown = mountHoloTilt(root);
  const glitch = mountRandomGlitch(Math.random, LANDING_GLITCH_KINDS);

  return () => {
    tiltTeardown();
    glitch.teardown();
    scan.remove();
    logoObserver?.disconnect();
    legalTeardown();
    root.removeEventListener('click', onClick);
    rosterEl?.removeEventListener('keydown', onRosterKeydown);
    document.removeEventListener('click', onDocClickForMenu);
    document.removeEventListener('keydown', onDocKeydownForMenu);
    clickTeardown();
    muteToggle.destroy();
    audioTeardown();
  };
}
